import type { AxiosInstance, AxiosRequestConfig } from 'axios';

import { toApiError } from './apiErrors';
import type { PageSdkRequestTracker } from './requestActivity';
import type { SdkApi } from './types';

export function createApi(
  axiosInstance: AxiosInstance,
  requestTracker?: PageSdkRequestTracker
): SdkApi {
  async function request<T>(
    method: 'get' | 'post' | 'put' | 'patch' | 'delete',
    url: string,
    dataOrConfig?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const finishRequest = requestTracker?.begin();

    try {
      if (method === 'get' || method === 'delete') {
        const response = await axiosInstance.request<T>({
          ...(dataOrConfig as AxiosRequestConfig | undefined),
          method,
          url
        });
        return response.data;
      }

      const response = await axiosInstance.request<T>({
        ...config,
        method,
        url,
        data: dataOrConfig
      });
      return response.data;
    } catch (error) {
      throw toApiError(error);
    } finally {
      finishRequest?.();
    }
  }

  return {
    get: <T = unknown>(url: string, config?: AxiosRequestConfig) => request<T>('get', url, config),
    post: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
      request<T>('post', url, data, config),
    put: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
      request<T>('put', url, data, config),
    patch: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
      request<T>('patch', url, data, config),
    delete: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
      request<T>('delete', url, config)
  };
}
